import { createContext, useContext } from 'react';
import type { Gender } from '@wix/ai-scheduling-assistant-common-lib/utils';

/**
 * The agent-name CONTEXT and its pure helpers, in a module with no runtime imports beyond React — so chrome that only
 * READS the name (the chat header, through `useGroupSummary`) does not pull common-lib's runtime graph into the unit
 * tests that render it (orion-web CLAUDE.md, "a unit test must not import the app's dependency graph").
 * `lib/use-agent-name.ts` provides the value and re-exports everything here, so its callers are unchanged.
 */

/**
 * The resolved name the UI should use for the primary assistant, so chrome ("Ask {short}", the chat
 * sender label, "Resolve with {short}") follows the name the user picked in Personality rather than a
 * hardcoded "Maestro".
 */
export interface AgentName {
  /** Full display name — "Maestro" / "Maestra" / a custom name. */
  name: string;
  /** Casual address form — the leading article stripped ("The Guide" → "Guide"). Same as `name` for a
   *  one-word custom name. This is the form for "Ask X" / "Talk to X" / "X's Brief". */
  short: string;
  /** The persona's presentation gender, for copy that must agree grammatically with the assistant (e.g.
   *  Hebrew "חושב" vs "חושבת"). Defaults to the unconfigured masculine default. */
  gender: Gender;
}

export function agentShortForm(name: string): string {
  return name.replace(/^the\s+/i, '');
}

/** The name with nothing configured — native's `AssistantName.defaultFull`. */
export const DEFAULT_ASSISTANT_NAME = 'Maestro';

/** The default's older English form. Group rows and cached names still carry it, so it reads as generic too. */
export const LEGACY_DEFAULT_ASSISTANT_NAME = 'The Maestro';

export const MAESTRO_DEFAULT: AgentName = { name: DEFAULT_ASSISTANT_NAME, short: 'Maestro', gender: 'male' };

export const AgentNameContext = createContext<AgentName>(MAESTRO_DEFAULT);

export function useAgentName(): AgentName {
  return useContext(AgentNameContext);
}
